import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { unescape } from 'lodash';
import PropTypes from 'prop-types';

import Button from '../../library/button/button';
import Page, { Breadcrumbs, Content, PageTitle } from '../../library/page/page';
import FitmentPageBanner from '../../components/fitment-page-banner/fitment-page-banner';
import SkeletonElement from '../../components/skeleton-element/skeleton-element';
import CategoryGrid from './components/category-grid/category-grid';
import CategoryList from './components/category-list/category-list';
import DefaultLayout from '../../layouts/default-layout/default-layout';
import { AngleDownLightIcon } from '../../shared/icons/icons';

import {
  useCategories,
  useDataLayer,
  useDoesBrandFit,
  useFitment
} from '../../shared/hooks/hooks';

import { metaTags } from '../../shared/utils/page/page';
import { ProductTypeProp } from '../../shared/utils/prop-types/prop-types';
import sortByName from '../../shared/utils/sort-by-name/sort-by-name';
import { isArrayWithLength } from '../../shared/utils/validators/validators';
import { shopAction } from './utils/analytics';
import {
  getCategoryPageBreadcrumbs,
  getCategoryURL
} from './utils/category-page-utils';

import {
  PAGE_NAME,
  PAGE_SECTION_NAME,
  PAGE_SUBCATEGORY,
  PAGE_TYPE
} from '../../shared/constants/data-layer';
import { DONT_FIT, FIT } from '../../shared/constants/fit';
import {
  PRODUCT_TYPES,
  TIRE_CATEGORIES,
  WHEEL_CATEGORIES
} from '../../shared/constants/products';
import { CATEGORY_PAGE_VIEW_TYPES } from '../../shared/constants/view-types';

import './category-page.scss';

function CategoryPage(props) {
  const {
    ctaText,
    seoName,
    productType,
    isTaxonomy,
    includeProducts,
    viewType,
    showLogo,
    includeNoFitCategories
  } = props;

  const {
    loading: isLoading,
    isProductsLoading,
    category,
    categoryData,
    noFitCategoryData,
    cms,
    products
  } = useCategories({
    includeNoFitCategories,
    includeProducts,
    isTaxonomy,
    productType,
    seoName
  });

  const { selectedFitment } = useFitment();
  const isStaggeredVehicle = selectedFitment?.isStaggered;

  let { doesFitEnum, isLoading: isLoadingDoesFit } = useDoesBrandFit({
    categoryCode: category?.code,
    productType,
    skipDoesFitQuery: includeNoFitCategories || isLoading
  });

  if (includeNoFitCategories && isArrayWithLength(categoryData)) {
    doesFitEnum = categoryData.length > 0 ? FIT : DONT_FIT;
  }

  const location = useLocation();
  const { updateDataLayer } = useDataLayer();
  const { description, documentTitle, metaTags: metaData } = cms?.page || {};
  const showFitmentPageBanner =
    seoName !== TIRE_CATEGORIES.TRAILER_TIRES &&
    seoName !== WHEEL_CATEGORIES.TRAILER_WHEELS;
  const [showNoFitCategories, setShowNoFitCategories] = useState(false);

  const toggleShowNoFitCategories = useCallback(() => {
    setShowNoFitCategories(true);
  }, []);

  const categories = isArrayWithLength(categoryData)
    ? categoryData
    : noFitCategoryData || [];

  const showShopAllButton =
    !window.isPreRendering &&
    !isLoading &&
    !isProductsLoading &&
    (typeof ctaText === 'function' || typeof ctaText === 'string') &&
    (isArrayWithLength(categoryData) || isArrayWithLength(noFitCategoryData));

  function getNoFitSubtitle() {
    if (isTaxonomy) {
      return `The following categories do not make products in the size configured to your selected vehicle.`;
    } else {
      return `The following brands do not make ${
        (productType === PRODUCT_TYPES.TIRES && 'tires') ||
        (productType === PRODUCT_TYPES.WHEELS && 'wheels')
      } that fit your selected vehicle.`;
    }
  }

  function getListCategories(categories) {
    return [...categories].sort(sortByName).reduce((acc, categoryItem) => {
      if (!categoryItem?.name || !categoryItem?.code) {
        return acc;
      }

      acc.push({
        ...categoryItem,
        linkText: `shop ${
          productType === PRODUCT_TYPES.ACCESSORIES ? '' : category?.name
        } ${categoryItem?.name?.replace(
          new RegExp(`${productType}`, 'gi'),
          ''
        )}`,
        products: (products?.[categoryItem.code] || []).sort(sortByName)
      });

      return acc;
    }, []);
  }

  useEffect(() => {
    if (!isLoading && category && documentTitle) {
      let pageName = 'tire_category';
      let pageSubCategory = category.name?.toLowerCase();

      if (productType === PRODUCT_TYPES.WHEELS) {
        pageName = 'wheel_category';
      } else if (productType === PRODUCT_TYPES.ACCESSORIES) {
        pageName = 'accessory_category';
      }

      if (category.code === 'allBrandsTires') {
        pageSubCategory = 'tires';
      } else if (category.code === 'allBrandsWheels') {
        pageSubCategory = 'wheels';
      }

      updateDataLayer({
        [PAGE_NAME]: pageName,
        [PAGE_SECTION_NAME]: documentTitle,
        [PAGE_SUBCATEGORY]: pageSubCategory,
        [PAGE_TYPE]: 'product_category'
      });
    }
  }, [category, productType, documentTitle, isLoading, updateDataLayer]);

  useEffect(() => {
    setShowNoFitCategories(false);
  }, [location.pathname]);

  return (
    <DefaultLayout>
      <Page>
        {showFitmentPageBanner && (
          <FitmentPageBanner
            doesFitEnum={doesFitEnum}
            isLoading={isLoadingDoesFit || isLoading}
            productType={productType}
          />
        )}
        <>
          {documentTitle && !window.isPreRendering && (
            <Helmet>
              <title>{unescape(documentTitle)}</title>
              {metaTags(metaData || [])}
            </Helmet>
          )}

          {!seoName?.includes('all-brands') && (
            <Breadcrumbs
              crumbs={getCategoryPageBreadcrumbs(location.pathname)}
            />
          )}

          {showLogo && (
            <div styleName="heading-image-container">
              {category?.thumbnail?.url && (
                <img
                  alt={category.thumbnail.altText}
                  height={37}
                  src={category.thumbnail.url}
                  width={110}
                />
              )}
            </div>
          )}

          {window.isPreRendering || isLoading ? (
            <>
              <SkeletonElement styleName="heading-skeleton" />
              <SkeletonElement styleName="sub-heading-skeleton" />
            </>
          ) : (
            <div
              styleName={
                viewType === CATEGORY_PAGE_VIEW_TYPES.PRODUCT_LIST
                  ? 'product-list-title-container'
                  : ''
              }
            >
              <PageTitle
                styleName="heading"
                subtitle={
                  !isLoading && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          productType === PRODUCT_TYPES.ACCESSORIES
                            ? category?.description
                            : description
                      }}
                    />
                  )
                }
                title={category?.name}
              >
                {viewType === CATEGORY_PAGE_VIEW_TYPES.PRODUCT_LIST &&
                  isArrayWithLength(categories) &&
                  showShopAllButton && (
                    <div styleName="shop-all-button-heading-container">
                      <Button
                        href={getCategoryURL(
                          `${window.location.pathname}-catalog`,
                          isStaggeredVehicle && doesFitEnum === FIT
                        )}
                        isPrimary
                        onClick={() =>
                          shopAction({
                            category,
                            parentCategory: category,
                            productType,
                            shopAll: true
                          })
                        }
                        styleName="shop-link"
                      >
                        {typeof ctaText === 'function'
                          ? ctaText(category, productType)
                          : ctaText}
                      </Button>
                    </div>
                  )}
              </PageTitle>
            </div>
          )}

          <Content styleName="content-container">
            {viewType === CATEGORY_PAGE_VIEW_TYPES.GRID &&
              !isLoading &&
              isArrayWithLength(categories) && (
                <CategoryGrid
                  categories={[...categories].sort(sortByName)}
                  parentCategory={category}
                  productType={productType}
                  showStaggeredURL={
                    isStaggeredVehicle &&
                    [PRODUCT_TYPES.TIRES, PRODUCT_TYPES.WHEELS].includes(
                      productType
                    )
                  }
                />
              )}
            {viewType !== CATEGORY_PAGE_VIEW_TYPES.GRID &&
              !isLoading &&
              isArrayWithLength(categories) && (
                <CategoryList
                  categories={getListCategories(categories)}
                  isProductList={
                    viewType === CATEGORY_PAGE_VIEW_TYPES.PRODUCT_LIST
                  }
                  parentCategory={category}
                  productType={productType}
                  showStaggeredURL={
                    isStaggeredVehicle &&
                    [PRODUCT_TYPES.TIRES, PRODUCT_TYPES.WHEELS].includes(
                      productType
                    )
                  }
                />
              )}
            {showShopAllButton && (
              <Button
                href={getCategoryURL(
                  `${window.location.pathname}-catalog`,
                  isStaggeredVehicle && doesFitEnum === FIT
                )}
                isPrimary={viewType === CATEGORY_PAGE_VIEW_TYPES.PRODUCT_LIST}
                isSecondary
                onClick={() =>
                  shopAction({
                    category,
                    parentCategory: category,
                    productType,
                    shopAll: true
                  })
                }
                styleName="shop-link"
              >
                {typeof ctaText === 'function'
                  ? ctaText(category, productType)
                  : ctaText}
              </Button>
            )}
            {(viewType === CATEGORY_PAGE_VIEW_TYPES.GRID || isTaxonomy) &&
              isArrayWithLength(noFitCategoryData) && (
                <>
                  {!showNoFitCategories && (
                    <Button
                      onClick={toggleShowNoFitCategories}
                      styleName="no-fit-toggle-button"
                    >
                      view {isTaxonomy ? 'accessories' : 'brands'} that
                      don&#39;t fit your vehicle{' '}
                      <AngleDownLightIcon styleName="no-fit-toggle-icon" />
                    </Button>
                  )}
                  {showNoFitCategories && (
                    <div styleName="no-fit-content">
                      <PageTitle
                        styleName={
                          isTaxonomy
                            ? 'no-fit-heading-accessory'
                            : 'no-fit-heading'
                        }
                        subtitle={getNoFitSubtitle()}
                        title={
                          isTaxonomy
                            ? "ACCESSORIES THAT DON'T FIT"
                            : "BRANDS THAT DON'T FIT"
                        }
                      />
                      {isTaxonomy ? (
                        <CategoryList
                          categories={getListCategories(noFitCategoryData)}
                          isProductList={
                            viewType === CATEGORY_PAGE_VIEW_TYPES.PRODUCT_LIST
                          }
                          parentCategory={category}
                          productType={productType}
                        />
                      ) : (
                        <CategoryGrid
                          categories={[...noFitCategoryData].sort(sortByName)}
                          styleName="no-fit-grid"
                        />
                      )}
                    </div>
                  )}
                </>
              )}
          </Content>
        </>
      </Page>
    </DefaultLayout>
  );
}

CategoryPage.propTypes = {
  ctaText: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  hideShopAllButton: PropTypes.bool,
  includeNoFitCategories: PropTypes.bool,
  includeProducts: PropTypes.bool,
  isTaxonomy: PropTypes.bool,
  productType: ProductTypeProp,
  seoName: PropTypes.string,
  showLogo: PropTypes.bool,
  viewType: PropTypes.oneOf([
    'simple-list',
    'product-list',
    'product-tag-list',
    'grid'
  ])
};

export default CategoryPage;
